import type { Mouvement } from '@mairistem/comptabilite';
import { PanelClass } from '@mairistem/finances-utils';
import type { ErrorField } from '@mairistem/liste';
import { isNil } from 'lodash';

/**
 * Base commune à tous les blocs (panels) de la fiche mouvement.
 *
 * Porte le contrôle bloquant générique : un bloc obligatoire doit être
 * intégralement renseigné. Un bloc multi-champs surcharge simplement
 * `isComplete` (prédicat, à l'image de `isEmpty`) en s'appuyant sur `allPresent`.
 */
class BasePanel extends PanelClass {
	/**
	 * Contrôle bloquant du bloc avant validation de la fiche.
	 *
	 * Règle métier : un bloc obligatoire doit être présent et renseigné à 100 %.
	 * Les blocs non obligatoires ne sont pas contrôlés.
	 * @param data Le mouvement en cours de saisie
	 * @returns Les erreurs empêchant la validation, vide si le bloc est valide
	 */
	check(data: Mouvement): ErrorField[] {
		if (!this.isMandatory) {
			return [];
		}

		if (this.isEmpty(data)) {
			return [
				{
					id: this.code,
					message: `Veuillez renseigner le bloc obligatoire "${this.getLibelle(
						data?.sens,
					)}".`,
				},
			];
		}

		return this.isComplete(data)
			? []
			: [
					{
						id: this.code,
						message: `Le bloc obligatoire "${this.getLibelle(
							data?.sens,
						)}" doit être entièrement renseigné.`,
					},
				];
	}

	/**
	 * Un bloc est "complet" quand tous ses champs sont renseignés. Par défaut
	 * (bloc mono-champ) c'est l'inverse de `isEmpty` ; les blocs multi-champs
	 * surchargent ce prédicat via `allPresent`.
	 * @param data Le mouvement en cours de saisie
	 */
	isComplete = (data: Mouvement) => !this.isEmpty(data);

	/**
	 * Vrai si toutes les valeurs sont renseignées. `0` compte comme renseigné ;
	 * seuls `null`, `undefined` et `''` sont considérés comme vides.
	 */
	protected allPresent(...values: unknown[]): boolean {
		return values.every((value) => !isNil(value) && value !== '');
	}
}

export default BasePanel;
